import React from 'react';
import styled from 'styled-components';
import { media } from '../../../../shared/lib/styles';
import { webOnboarderGetStartedPath } from '../../../../shared/lib/platformUrls';
import Box from '../../../../shared/spa/core-components/Box';
import Button from '@99designs/frontend-components/components/Button/Button';
import Attribution from '../../../../shared/components/Attribution';
import BreadCrumbs from '../../../../shared/components/BreadCrumbs/BreadCrumbs';
import ContentSideFeature from '../../../../shared/components/ContentSideFeature';
import CtaPair from '../../../../shared/components/CtaPair';
import Heading from '../../../../shared/components/Heading';
import Image from '../../../../shared/components/Image';
import { CheckList } from '../../../../shared/components/List';
import Paragraph from '../../../../shared/components/Paragraph';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import Grid from '../../Grid';

const StyledHeroSide = styled(Box)`
    height: 100%;
    position: relative;
`;

const ImageContainer = styled(Box)`
    @media ${media('normal', 'large')} {
        position: absolute;
        right: 0;
        min-width: 824px;
    }
`;

const heroColor = '#079B7F';

function HeroSide({ languageCode }: Props) {
    let designerName = 'FaTiH™';
    let imageUrl = `website-project/hero/en/hero01.png`;
    if (languageCode === 'fr') {
        imageUrl = `website-project/hero/fr/hero01.png`;
        designerName = 'Random Designer';
    }
    return (
        <StyledHeroSide>
            <ImageContainer paddingRight={['none', 'gutter']}>
                <Image src={publicImageUrl(imageUrl)} alt={__('Website example on laptop')} />
                <Box display="flex" justifyContent="end" paddingRight={['none', 'space15']}>
                    <Attribution name={designerName} />
                </Box>
            </ImageContainer>
        </StyledHeroSide>
    );
}

function HeroTop({ languageCode }: Props) {
    let heroText = __(
        'Creating a website doesn’t have to be difficult. We’ll help you bring your website to life, so you can stay focused on running your business.'
    );

    let heroCheckList = [
        __('Affordable to premium pricing options'),
        __('Easy setup and support'),
        __('Work with professional designers'),
    ];

    if (languageCode === 'fr') {
        heroText = `Créer un site web n'est pas toujours aussi compliqué qu'on le croit. Nous vous aidons à donner vie à votre site internet, pour que vous puissiez vous concentrer sur votre entreprise. Répondez à notre questionnaire en ligne pour vous lancer!`;
        heroCheckList = [
            'Gamme de prix de bon marché à premium',
            'Assistance dans le choix du créateur de site web',
            'Vaste sélection de designers professionnels',
        ];
    }

    return (
        <Box paddingLeft={['none', 'gutter']}>
            <Heading
                style={{ color: heroColor }}
                type="h1"
                variations={['h0', 'h0-capped', 'with-divider']}
            >
                {__('Get online fast with a sleek, new website')}
            </Heading>
            <Box paddingLeft="gutter">
                <Paragraph type="div" variations={['narrow-widescreen']}>
                    <Paragraph>{heroText}</Paragraph>
                    <CheckList items={heroCheckList} />
                </Paragraph>
            </Box>
        </Box>
    );
}

function HeroBottom() {
    return (
        <Box style={{ color: heroColor }} paddingLeft={['none', 'gutter']}>
            <CtaPair
                variations={['chameleon', 'responsive', 'jumbo', 'no-margin']}
                primary={{
                    label: __('Get started'),
                    url: webOnboarderGetStartedPath(),
                    variations: ['jumbo', 'primary', 'chameleon', 'responsive'],
                }}
                secondary={
                    <Button
                        href="#how-it-works"
                        variations={['jumbo', 'marketing-secondary', 'chameleon', 'responsive']}
                    >
                        {__('How it works')}
                    </Button>
                }
            />
        </Box>
    );
}

interface Props {
    languageCode: string;
}

function HeroSection({ languageCode }: Props) {
    return (
        <>
            <Grid>
                <BreadCrumbs
                    breadcrumbs={[
                        {
                            label: __('Home'),
                            path: '/',
                        },
                        {
                            label: __('Website builders'),
                        },
                    ]}
                />
            </Grid>

            <Box paddingTop="space14" paddingBottom="space14">
                <div className="row">
                    <div className="column large-12 normal-12 small-12 medium-8 medium-offset-2">
                        <ContentSideFeature
                            side={<HeroSide languageCode={languageCode} />}
                            top={<HeroTop languageCode={languageCode} />}
                            bottom={<HeroBottom />}
                        />
                    </div>
                </div>
            </Box>
        </>
    );
}

export default HeroSection;
